import {assoc, curry, is, isNil, keys, map, reduce, reject, when} from 'ramda'

/**
 * Creates a new object with the own properties of the provided object, but the
 * keys renamed according to the keysMap object as `{oldKey: newKey}`.
 * When some key is not found in the keysMap, then it's passed as-is.
 *
 * Example: {a: b} -> {a: *} -> {b: *}
 */
export const renameKeys = curry((keysMap, obj) =>
  reduce((acc, key) => assoc(keysMap[key] || key, obj[key], acc), {}, keys(obj))
)

/**
 * Safely parses a JSON string, returning an empty object if parsing fails.
 * @param obj - The object/string to parse as JSON
 * @returns The parsed object or empty object if parsing fails
 */
export const safeJsonParse = <T = unknown>(obj: unknown): T => {
  try {
    const str = obj as string
    return JSON.parse(str) as T
  } catch {
    return {} as T
  }
}

/**
 * Creates a proxy object that allows to access object properties by any-case keys
 * @param obj - The object to make case-insensitive
 * @returns Proxy object with case-insensitive key access
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const removeKeyCaseSensitivity = <T extends Record<string, any>>(
  obj: T
) => {
  const lowercaseKeysObject: T = Object.keys(obj).reduce(
    (acc, key) => {
      acc[key.toLowerCase()] = obj[key]
      return acc
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    {} as Record<any, any>
  )

  return new Proxy(obj, {
    get: (_, key: string) => {
      if (key.toLowerCase() in lowercaseKeysObject) {
        return lowercaseKeysObject[(key as string).toLowerCase()]
      }
    }
  })
}

/**
 * Removes nullish string values from the object
 * @param obj - object to sanitize
 * @returns sanitized object
 */
export const sanitizeNullishStrings = (obj: Record<string, string>) =>
  reject(value => value === 'null' || value === 'undefined', obj)

/**
 * Remove nullish values from an object
 */
export const removeEmpty = <T extends object>(obj: T): T =>
  Object.fromEntries(
    Object.entries(obj).filter(([, value]) => !isNil(value))
  ) as T

/**
 * Recursively removes null or undefined values from an object
 * @param obj - The object to remove empty values from
 * @returns The object with empty values removed
 */
export const removeEmptyRecursive = <T extends object>(obj: T): T => {
  if (isNil(obj) || typeof obj !== 'object') return obj

  return map(when(is(Object), removeEmptyRecursive), reject(isNil, obj))
}
